import React, { JSX, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Button, FormControlLabel, Radio, RadioGroup,
  TextField,
  DialogContent
} from '@mui/material';
import { TransactionDetails } from '../types/StockhornTypes';
import SkeletonTable from './loaders/SkeletonTable';
import { TableForEditing } from './TableForEditing';
import {Cookies}  from '../js/localStorageCode';
const cookies = new Cookies();

interface TextInputModalProps {
  open: boolean;
  onClose: () => void;
  contents?: string;
}

interface SelectInputModalProps {
  open: boolean;
  onClose: () => void;
  selectedColumn:  (val:string) => void;
  originalFields: string[];
}

interface SelectStoredEntriesModalProps {
  open: boolean;
  storedData?: Record<string, any>;
  onClose: () => void;
  options: string[];
  onSelect: (val: TransactionDetails[]) => void;
}
interface TableRow {
  id: string;
  name: string;
  value: number;
}

interface FinalData {
  rows: TransactionDetails[];
  amount: string;
  [key: string]: any; // For any additional metadata
}

interface ComputedValues {
  expires: number;
  value: FinalData;
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: TableRow[] | ComputedValues | null;
  selectedKey: string;
  isLoading?: boolean;
}
export const CopyToClipboardModal: React.FC<TextInputModalProps> = ({
  open,
  onClose,
  contents = '',
}) => {



  const handleCancel = () => {
    onClose();
  };

  const toClipboard = () => {
    navigator.clipboard.writeText(contents)
      .then(() => {
        handleCancel();
      })
      .catch((err) => {
        alert('Failed to copy text');
        handleCancel();
      });
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{contents}</DialogTitle>
      <DialogActions>
        <Button onClick={toClipboard} color="secondary">
          Copy to Clipboard
        </Button>
        <Button onClick={handleCancel} color="secondary">
          Exit
        </Button>
      </DialogActions>
    </Dialog>
  );
};


export const SelectFieldToFilterByModal: React.FC<SelectInputModalProps> = ({
  open,
  onClose,
  selectedColumn,
  originalFields
}) => {

  const [selectedColumnOption, setSelectedColumnOption] = useState<string>('Description');

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Select Column to Filter:</DialogTitle>
        <RadioGroup
          className="radioGroup"
          value={selectedColumnOption}
          onChange={(e) => setSelectedColumnOption(e.target.value)}
        >
          {originalFields.sort().map((option) => (
            <FormControlLabel
              key={option}
              value={option}
              control={<Radio />}
              label={option}
            />
          ))}
        </RadioGroup>
        <DialogActions>
          <Button onClick={()=>{selectedColumn(selectedColumnOption);onClose();}} color="secondary">
            Select
          </Button>
          <Button onClick={handleCancel} color="secondary">
            Exit
          </Button>
      </DialogActions>
    </Dialog>
  );
};


export const SelectStoredEntriesModal: React.FC<SelectStoredEntriesModalProps> = ({
  open,
  storedData,
  onClose,
  options,
  onSelect
}) => {
  const [selectedKey, setSelectedKey] = useState<string>('');
  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const key = event.target.value;
    setSelectedKey(key);
    onSelect(storedData? storedData[key].value.rows: []);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Select Data</DialogTitle>
        <div className="radioGroup">
          <select className="storedDataSelect" value={selectedKey} onChange={handleChange}>
            {options.length === 0 && <option value="">No data available</option>}
            {options.map((key) => (
              <option key={key} value={key}>
                {key}
              </option>
            ))}
          </select>
        </div>
     </Dialog>
  );
};

export const DataSummaryModal: React.FC<ModalProps> = ({ isOpen, onClose, data, selectedKey, isLoading = false }) => {
  const [editData, setEditData] = useState<boolean>(false);
  const [viewData, setViewData] = useState<boolean>(false);
  if (!isOpen) return null;

  // Summarize table data
  const summarizeTableData = (tableData: TableRow[]): JSX.Element => {
    const rowCount = tableData.length;
    const totalValue = tableData.reduce((sum, row) => sum + Math.abs(row.value), 0);
    const averageValue = rowCount > 0 ? totalValue / rowCount : 0;

    return (
      <div>
        <h3>Table Data Summary</h3>
        <p><strong>Key:</strong> {selectedKey}</p>
        <p><strong>Row Count:</strong> {rowCount}</p>
        <p><strong>Total Value:</strong> {totalValue}</p>
        <p><strong>Average Value:</strong> {averageValue.toFixed(2)}</p>
      </div>
    );
  };
 // Summarize computed values
 const summarizeComputedValues = (computed: ComputedValues): JSX.Element => {
  
      const myData = computed.value;
      const amount = parseFloat(myData.amount).toFixed(2);
      const averageValue = myData.rows.length > 0 ? parseFloat(Math.abs((Number(myData.amount)) / myData.rows.length).toFixed(2)) : 0;
      const toClipboard = () => {
        navigator.clipboard.writeText(amount)
          .then(() => {})
          .catch((err) => {
            alert('Failed to copy text');
          });
      };
      const createExpense = () => {
        const expenseDescription: string | null = window.prompt('Enter a Description of the Expense:');
        if(expenseDescription){
          const partial: boolean = window.confirm('Is this a partial expense based on the 15% contingency?');
          const name = 'EXPENSE_CATEGORY-'+ new Date().toISOString().split('.')[0]+'_'+expenseDescription+ (partial?'_partialExpense15':'');
          cookies.setWithReducer(name, myData.rows, {columnFiltered:myData.columnFiltered, filterUsed:myData.filterUsed}, {expires: 365});
        }
      } 

      const deleteKey = (key:string) => {
          const partial: boolean = window.confirm(`YOu sure you want to delete the key ${key}?`);
          if(partial){
            cookies.removeSingleNameValuePair(key);
            onClose();
          }
      }


      return (
        <div>
          <h3>Computed Values Summary</h3>
          <p><strong>Key:</strong> {selectedKey}&nbsp;&nbsp;&nbsp;<button onClick={()=>{deleteKey(selectedKey)}}>Delete Key</button></p>
          <p>The total amount is <span onClick={toClipboard} className={'copyToClipboard'}>{amount}&#x1f4cb;</span> over {myData.rows.length} purchases</p>
          <p>That's an average of {averageValue} per purchase</p>
          {!viewData &&<><button onClick={()=>{setViewData(prev => !prev);setEditData(false)}}>View Data</button>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</>}
           {!editData &&<><button onClick={()=>{setEditData(prev => !prev);setViewData(false)}}>Edit Data</button>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</>}
          <button onClick={createExpense}>Create Expense</button>
          {(editData || viewData)&& (
            <TableForEditing data={myData} length={myData.rows.length} onClose={()=>{setEditData(prev => !prev)}} edit={editData}/>
          )}
        </div>
      );
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close" onClick={onClose} aria-label="Close modal">
          &times;
        </button>
        {isLoading ? (
          <SkeletonTable rows={3} columns={2} />
        ) : data ? (
          Array.isArray(data) ? summarizeTableData(data) : summarizeComputedValues(data)
        ) : (
          <p>No data available for {selectedKey}</p>
        )}
      </div>
    </div>
  );
};


interface NameValueModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any;
  keyValue: string;
}

export const NameValueModal: React.FC<NameValueModalProps> = ({
  isOpen,
  onClose,
  data, 
  keyValue
}) => {

  const handleCancel = () => {
    onClose();
  };

  const toClipboard = () => {
    navigator.clipboard.writeText(JSON.stringify(data[keyValue].value, replacer, 2))
      .then(() => {
      })
      .catch((err) => {
        alert('Failed to copy text');
        handleCancel();
      });
  };
  const handleDelete = () => {
    const partial: boolean = window.confirm('Are you sure you want to delete this category?');
    if(partial){
      cookies.removeSingleNameValuePair(keyValue);
      onClose();
    }
  };

  const replacer = (key: string, value: any): any => {
    return key === 'expires' || key === 'rows' ? undefined : value; 
  };

  const value = data[keyValue]&& data[keyValue].value? JSON.stringify(data[keyValue].value, replacer, 2): 'No data found';

  return (
    <Dialog open={isOpen} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle>NAME:{keyValue}</DialogTitle>
      <DialogTitle>VALUE:{value}</DialogTitle>
      <DialogActions>
        <Button onClick={toClipboard} color="secondary">
          Copy To Clipboard
        </Button>
        <Button onClick={handleCancel} color="secondary">
          Exit
        </Button>
        <Button onClick={handleDelete} color="secondary">
          DELETE
        </Button>
      </DialogActions>
    </Dialog>
  );
}
  interface TrackNewExpenseModalProps {
    isOpen: boolean;
    onSubmit: (value1: string, value2: string) => void;
    onClose: () => void;
  }

  export const TrackNewExpenseModal: React.FC<TrackNewExpenseModalProps> = ({ isOpen, onSubmit, onClose }) => {
    const [expense, setExpense] = useState('');
    const [details, setDetails] = useState('');

    const handleSubmit = () => {
      if (expense.trim() && details.trim()) {
        onSubmit(expense, details);
        setExpense('');
        setDetails('');
        onClose();
      }
    };

    return (
      <Dialog open={isOpen} onClose={onClose}>
        <DialogTitle>Add the Expense Name and some Details about it</DialogTitle>
        <DialogContent>
          <TextField
            label="Enter a Name for the Expense"
            value={expense}
            onChange={(e) => setExpense(e.target.value)}
            className={'input'}
            fullWidth
            autoFocus
            margin="dense"
          />
          <TextField
            label="Enter Details about the Expense"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            className={'input'}
            fullWidth
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!expense.trim() || !details.trim()}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
